"use client"

import { useState, useCallback } from "react"
import Link from "next/link"
import { formatValue, shuffleArray } from "@/lib/utils"
import { RotateCcw, Share2, Check, Play } from "lucide-react"

interface Item { name: string; slug: string | null; value: number }

interface Props {
  items: Item[]
  valueLabel: string
  valueUnit?: string
  valuePrefix: boolean
  typeName: string
  categorySlug: string
  typeSlug: string
}

function getAccuracy(guess: number, actual: number): { pct: number; label: string; color: string } {
  const pctOff = guess === actual ? 0 : actual === 0 ? Infinity : Math.abs(1 - guess / actual) * 100
  if (pctOff <= 5) return { pct: 100, label: "Bullseye!", color: "text-emerald-400" }
  if (pctOff <= 15) return { pct: 80, label: "Very close!", color: "text-emerald-400" }
  if (pctOff <= 30) return { pct: 60, label: "Close", color: "text-cyan-400" }
  if (pctOff <= 50) return { pct: 40, label: "Not bad", color: "text-amber-400" }
  return { pct: 20, label: "Way off", color: "text-rose-400" }
}

export function GuessTheValueGame({ items, valueLabel, valueUnit, valuePrefix, typeName, categorySlug, typeSlug }: Props) {
  const [rounds, setRounds] = useState(() => items.slice(0, 5))
  const [currentRound, setCurrentRound] = useState(0)
  const [input, setInput] = useState("")
  const [guessed, setGuessed] = useState(false)
  const [results, setResults] = useState<{ item: Item; guess: number; accuracy: ReturnType<typeof getAccuracy> }[]>([])
  const [copied, setCopied] = useState(false)
  const [shareError, setShareError] = useState(false)

  const fmt = (v: number) => formatValue(v, valueUnit, valuePrefix)
  const current = rounds[currentRound]
  const isFinished = currentRound >= rounds.length

  const handleGuess = useCallback(() => {
    const guess = Number(input)
    if (!current || guessed || !input.trim() || !Number.isFinite(guess)) return
    const accuracy = getAccuracy(guess, current.value)
    setResults((prev) => [...prev, { item: current, guess, accuracy }])
    setGuessed(true)
  }, [input, current, guessed])

  const nextRound = () => {
    setCurrentRound((prev) => prev + 1)
    setInput("")
    setGuessed(false)
  }

  const reset = () => {
    setRounds(shuffleArray(items).slice(0, 5))
    setCurrentRound(0)
    setInput("")
    setGuessed(false)
    setResults([])
  }

  const totalScore = results.reduce((s, r) => s + r.accuracy.pct, 0)
  const maxScore = results.length * 100

  const share = async () => {
    const text = `Guess the ${valueLabel} (${typeName}): ${totalScore}/${maxScore} points!\n\n${results.map((r) => `${r.item.name}: ${r.accuracy.label}`).join("\n")}\n\nTry it: playhigherlower.com/guess-the/${categorySlug}/${typeSlug}`
    if (navigator.share) {
      try { await navigator.share({ title: `Guess the ${valueLabel}`, text }); return } catch { /* fallback */ }
    }
    try {
      await navigator.clipboard.writeText(text)
      setShareError(false)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setShareError(true)
    }
  }

  if (isFinished) {
    return (
      <div className="space-y-6">
        <div className="p-8 rounded-2xl bg-rose-500/5 border border-rose-500/20 text-center">
          <h2 className="text-2xl font-black text-white mb-2">Final Score</h2>
          <p className="text-4xl font-black text-rose-400">{totalScore}/{maxScore}</p>
        </div>

        <div className="space-y-2">
          {results.map((r, i) => (
            <div key={i} className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 break-words">
                  <span className="text-white font-medium">{r.item.name}</span>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Your guess: {fmt(r.guess)} · Actual: {fmt(r.item.value)}
                  </div>
                </div>
                <span className={`text-sm font-bold ${r.accuracy.color}`}>{r.accuracy.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={reset} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white font-medium hover:bg-zinc-700 transition-colors">
            <RotateCcw className="w-4 h-4" /> Play again
          </button>
          <button onClick={share} className={`flex items-center justify-center gap-2 px-5 py-3 rounded-xl border font-medium transition-all ${copied ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-400" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white"}`}>
            {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            {copied ? "Copied" : "Share"}
          </button>
        </div>

        {shareError && <p role="status">Sharing is unavailable. Copy this page’s address to share the game.</p>}
        <Link href={`/play/${categorySlug}/${typeSlug}`} className="flex items-center justify-center gap-2 py-4 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-colors">
          <Play className="w-5 h-5" /> Play the Higher Lower game →
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-zinc-400">Round {currentRound + 1} of {rounds.length}</span>
        {results.length > 0 && <span className="text-sm text-zinc-500">Score: {totalScore}</span>}
      </div>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className="h-full rounded-full bg-rose-500 transition-all duration-300" style={{ width: `${(currentRound / rounds.length) * 100}%` }} />
      </div>

      {current && (
        <div className="p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
          <p className="text-sm text-zinc-500 mb-2">What is the {valueLabel.toLowerCase()} of</p>
          <h2 className="text-3xl font-black text-white mb-6">{current.name}</h2>

          {!guessed ? (
            <div className="max-w-sm mx-auto space-y-4">
              <div className="relative">
                {valueUnit && valuePrefix && (
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 font-medium">{valueUnit}</span>
                )}
                <input
                  type="number"
                  step="any"
                  aria-label={`Your guess for ${current.name}`}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleGuess()}
                  placeholder="Your guess..."
                  className={`w-full px-4 py-4 rounded-xl bg-zinc-800 border border-zinc-700 text-white text-lg font-medium text-center placeholder:text-zinc-600 focus:outline-none focus:border-rose-500/50 transition-colors ${valueUnit && valuePrefix ? "pl-10" : ""}`}
                />
              </div>
              <button
                onClick={handleGuess}
                disabled={!input.trim() || !Number.isFinite(Number(input))}
                className="w-full py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-semibold transition-colors"
              >
                Lock in guess
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className={`text-lg font-bold ${results[results.length - 1]?.accuracy.color}`}>
                {results[results.length - 1]?.accuracy.label}
              </div>
              <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
                <div className="p-3 rounded-xl bg-zinc-800">
                  <div className="text-xs text-zinc-500">Your guess</div>
                  <div className="text-white font-bold">{fmt(results[results.length - 1]?.guess ?? 0)}</div>
                </div>
                <div className="p-3 rounded-xl bg-zinc-800">
                  <div className="text-xs text-zinc-500">Actual</div>
                  <div className="text-rose-400 font-bold">{fmt(current.value)}</div>
                </div>
              </div>
              <button
                onClick={nextRound}
                className="px-6 py-2 rounded-xl bg-zinc-700 hover:bg-zinc-600 text-white font-medium transition-colors"
              >
                {currentRound + 1 < rounds.length ? "Next round →" : "See results"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
